// The navigation rail. Hovering an item opens its flyout: the page's name and what is inside
// it, rendered server-side in a <template data-n-flyout-for="…"> next to the item. The flyout
// glides between items instead of closing and reopening, and a bar slides to the current page.
// The Inbox badge refreshes itself from GET /Inbox/Count every minute while the page is in view.
import { $, $$ } from './dom';

type InboxCounts = { waiting: number; approvals: number; connections: number; syncs: number };

/** Which count each Inbox flyout row shows (the rows' hrefs, from NavigationCatalog). */
const inboxRows: Record<string, keyof InboxCounts> = {
	'/Inbox': 'waiting',
	'/Inbox?kind=approval': 'approvals',
	'/Inbox?kind=connection': 'connections',
	'/Inbox?kind=sync': 'syncs',
};

function watchInbox(rail: HTMLElement): void {
	const badge = $('[data-n-inbox-count]', rail);
	const link = badge?.closest<HTMLElement>('.n-rail-item');
	if (!badge || !link) return;
	const label = $('.n-rail-label', link)?.textContent?.trim() ?? 'Inbox';
	const template = $<HTMLTemplateElement>('template[data-n-flyout-for="inbox"]', rail);

	const paint = (counts: InboxCounts) => {
		badge.textContent = counts.waiting > 99 ? '99+' : String(counts.waiting);
		badge.hidden = counts.waiting === 0;
		if (counts.waiting > 0) link.setAttribute('aria-label', `${label}, ${counts.waiting} waiting`);
		else link.removeAttribute('aria-label');
		if (!template) return;
		for (const end of $$('[data-n-count-for]', template.content)) {
			const key = inboxRows[end.dataset.nCountFor ?? ''];
			if (key) end.textContent = counts[key].toLocaleString();
		}
		const sub = $('[data-n-flyout-sub="inbox"]', template.content);
		if (sub)
			sub.textContent =
				counts.waiting === 0
					? 'Nothing is waiting for a person.'
					: `${counts.waiting} ${counts.waiting === 1 ? 'thing is' : 'things are'} waiting for a person.`;
	};

	const refresh = async () => {
		if (document.visibilityState !== 'visible') return;
		try {
			const response = await fetch('/Inbox/Count', {
				headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
				credentials: 'same-origin',
			});
			if (response.ok) paint((await response.json()) as InboxCounts);
		} catch {
			/* offline or signed out: keep the last count */
		}
	};
	window.setInterval(() => void refresh(), 60_000);
	document.addEventListener('visibilitychange', () => void refresh());
}

export function initRail(): void {
	const rail = $('[data-n-rail]');
	const flyout = $('#n-flyout');
	if (!rail || !flyout) return;

	watchInbox(rail);

	const indicator = $('[data-n-rail-indicator]', rail);
	const current = $('.n-rail-item[aria-current]', rail);
	if (indicator && current) {
		indicator.style.top = `${current.offsetTop + current.offsetHeight / 2 - 12}px`;
		indicator.dataset.visible = '';
	}

	let openFor: string | null = null;
	let showTimer = 0;
	let hideTimer = 0;

	const place = (item: HTMLElement) => {
		const r = item.getBoundingClientRect();
		const railBox = rail.getBoundingClientRect();
		flyout.style.left = `${railBox.right + 12}px`;
		const top = Math.max(14, Math.min(window.innerHeight - flyout.offsetHeight - 14, r.top + r.height / 2 - 28));
		flyout.style.top = `${top}px`;
		flyout.style.setProperty('--n-flyout-nub', `${r.top + r.height / 2 - top}px`);
	};
	const open = (item: HTMLElement) => {
		const key = item.dataset.nFlyout!;
		if (openFor !== key) {
			const template = $<HTMLTemplateElement>(`template[data-n-flyout-for="${key}"]`, rail);
			if (!template) return;
			flyout.replaceChildren(template.content.cloneNode(true));
			flyout.toggleAttribute('data-mini', template.hasAttribute('data-mini'));
			openFor = key;
		}
		place(item);
		flyout.dataset.open = '';
	};
	const close = () => {
		delete flyout.dataset.open;
		openFor = null;
	};

	rail.addEventListener('mouseover', (e) => {
		const item = (e.target as Element).closest<HTMLElement>('[data-n-flyout]');
		clearTimeout(hideTimer);
		if (!item) return;
		clearTimeout(showTimer);
		showTimer = window.setTimeout(() => open(item), flyout.hasAttribute('data-open') ? 0 : 120);
	});
	const leave = () => {
		clearTimeout(showTimer);
		hideTimer = window.setTimeout(close, 180);
	};
	rail.addEventListener('mouseleave', leave);
	flyout.addEventListener('mouseenter', () => clearTimeout(hideTimer));
	flyout.addEventListener('mouseleave', leave);
	rail.addEventListener('focusin', (e) => {
		const item = (e.target as Element).closest<HTMLElement>('[data-n-flyout]');
		if (item) open(item);
	});
	document.addEventListener('keydown', (e) => e.key === 'Escape' && close());
	document.addEventListener('click', (e) => {
		if ((e.target as Element).closest('#n-flyout a, [data-n-rail] a, [data-n-menu-trigger]')) close();
	});
	for (const item of $$('[data-n-flyout]', rail)) item.addEventListener('blur', leave);
}
